import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_COMPANY_SETTINGS,
  type CompanySettings,
  type UpdateCompanySettings,
} from '@geotracker/contracts';
import { api } from './client';

export const SETTINGS_KEY = ['settings'] as const;

/**
 * The company's panel settings.
 *
 * Long `staleTime`: these change when an administrator edits them, which is
 * roughly never, and the live map re-renders every fifteen seconds. Refetching
 * settings on that cadence would triple the map's request count to learn
 * nothing.
 *
 * `placeholderData` rather than a loading branch at every call site. Nothing
 * here blocks a first paint — the map draws on OpenStreetMap with the schema
 * defaults and swaps to the company's basemap when the real values land, which
 * is a better first frame than a spinner over an empty rectangle.
 */
export function useCompanySettings() {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => api.get<CompanySettings>('/admin/settings'),
    staleTime: 5 * 60_000,
    placeholderData: { data: DEFAULT_COMPANY_SETTINGS, message: '', meta: {} as never },
    select: (result) => result.data,
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patch: UpdateCompanySettings) =>
      api.put<CompanySettings>('/admin/settings', patch),
    onSuccess: (result) => {
      // The server returns the settled object, so the cache is set from the
      // response rather than invalidated — no refetch, and the map picks the
      // new basemap up on the same tick the save completes.
      queryClient.setQueryData(SETTINGS_KEY, result);
      // A changed delivery threshold reclassifies every point on every route.
      void queryClient.invalidateQueries({ queryKey: ['route'] });
    },
  });
}
